import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_VisibilityState,
} from 'material-react-table';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getNotes, deleteNote } from '../../api/notes';
import { Note } from '../../types/note';
import { TagChips } from '../../components/workflow/TagsInput';
import { useFocusSyncToast } from '../../utils/useFocusSyncToast';
import { formatAppDate } from '../../utils/dateFormats';

const noteViewHref = (id: string) => `/notes/${id}`;
const noteEditHref = (id: string) => `/notes/${id}/edit`;

/** Invisible full-cell link: right-click works on empty padding too, text stays unstyled. */
const NoteCellOverlay: React.FC<{
  id: string;
  name: string;
  isPrimary?: boolean;
  children: React.ReactNode;
}> = ({ id, name, isPrimary, children }) => (
  <Box
    sx={{
      position: 'relative',
      height: '100%',
      minHeight: 52,
      p: 2,
    }}
  >
    <Box
      component="a"
      href={noteEditHref(id)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={isPrimary ? `${name}` : undefined}
      aria-hidden={!isPrimary}
      tabIndex={isPrimary ? 0 : -1}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
      }}
    />
    <Box component="span" sx={{ position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
      {children}
    </Box>
  </Box>
);

export const NotesList: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const location = useLocation();

  const LOCAL_STORAGE_NOTES_KEY = 'notes_column_filters';
  const LOCAL_STORAGE_NOTES_COLUMN_VISIBILITY_KEY = 'notes_column_visibility';

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_NOTES_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
  });

  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_NOTES_COLUMN_VISIBILITY_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {};
      }
    }
    return {};
  });

  useEffect(() => {
    document.title = t('notes.title');
  }, [t]);

  useEffect(() => {
    if (location.state?.success) {
      enqueueSnackbar(location.state.success, { variant: 'success' });
      window.history.replaceState({}, document.title);
    }
  }, [location, enqueueSnackbar]);

  const {
    data: { notes = [], meta } = {},
    isError,
    isRefetching,
    isLoading,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: [
      'notes-list',
      {
        columnFilters,
        pagination,
        sorting,
      },
    ],
    queryFn: async () => {
      let filters = null;
      if (columnFilters) {
        filters = buildFilterParams(columnFilters);
      }
      let sort = null;
      if (sorting && sorting.length) {
        sort = buildSortParams(sorting);
      } else {
        sort = 'sort[0]=updated_at:desc'
      }
      return getNotes({
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        ...(filters && { filters }),
        ...(sort && { sort }),
      });
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  useFocusSyncToast({
    dataUpdatedAt,
    isRefetching,
    message: 'Notes synced from another window',
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNote,
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      enqueueSnackbar(t('notes.deleteSuccess'), { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(t('notes.deleteError'), { variant: 'error' });
    }
  });

  const columns = useMemo<MRT_ColumnDef<Note>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        enableEditing: false,
        size: 80,
        Cell: ({ cell, row }) => (
          <NoteCellOverlay id={row.original.id} name={row.original.name} isPrimary>
            {cell.getValue<string>()}
          </NoteCellOverlay>
        ),
      },
      {
        accessorKey: 'name',
        header: t('notes.name'),
        size: 200,
        Cell: ({ cell, row }) => (
          <NoteCellOverlay id={row.original.id} name={row.original.name}>
            {cell.getValue<string>()}
          </NoteCellOverlay>
        ),
      },
      {
        accessorKey: 'status',
        header: t('notes.status'),
        size: 140,
        Cell: ({ cell, row }) => (
          <NoteCellOverlay id={row.original.id} name={row.original.name}>
            {cell.getValue<string>() || '—'}
          </NoteCellOverlay>
        ),
      },
      {
        id: 'tags',
        accessorFn: (row) => (row.tags ?? []).map((tag) => tag.name).join(', '),
        header: t('tags.title'),
        size: 160,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <NoteCellOverlay id={row.original.id} name={row.original.name}>
            <TagChips tags={row.original.tags} max={3} />
          </NoteCellOverlay>
        ),
      },
      {
        accessorKey: 'description',
        header: t('notes.description'),
        Cell: ({ cell, row }) => {
          const description = cell.getValue<string>();
          const plainText = description.replace(/<[^>]*>/g, '');
          const text = plainText.length > 100 ? `${plainText.substring(0, 100)}...` : plainText;
          return (
            <NoteCellOverlay id={row.original.id} name={row.original.name}>
              {text}
            </NoteCellOverlay>
          );
        },
        size: 300,
      },
      {
        accessorKey: 'created_at',
        header: t('notes.createdAt'),
        Cell: ({ cell, row }) => (
          <NoteCellOverlay id={row.original.id} name={row.original.name}>
            {formatAppDate(cell.getValue<string>())}
          </NoteCellOverlay>
        ),
        filterVariant: 'date-range',
        size: 150,
      },
    ],
    [t],
  );

  const table = useMaterialReactTable({
    columns,
    data: notes,
    initialState: {
      showColumnFilters: true,
      columnPinning: { right: ['mrt-row-actions'] },
    },
    enableGlobalFilter: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    enableClickToCopy: 'context-menu',
    muiToolbarAlertBannerProps: isError
      ? {
          color: 'error',
          children: t('common.errorFetchingData'),
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: () => (
      <Tooltip arrow title={t('common.refreshData')}>
        <IconButton onClick={() => refetch()}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '1rem', position: 'relative', zIndex: 2 }}>
        <Tooltip title={t('notes.view')}>
          <IconButton
            component="a"
            href={noteViewHref(row.original.id)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('notes.view')}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('notes.editNote')}>
          <IconButton
            component="a"
            href={noteEditHref(row.original.id)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('notes.editNote')}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('notes.deleteTitle')}>
          <IconButton
            color="error"
            onClick={() => handleDelete(row.original.id, row.original.name)}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableBodyCellProps: ({ column }) =>
      column.id === 'mrt-row-actions'
        ? { sx: { position: 'relative', zIndex: 2, verticalAlign: 'middle' } }
        : { sx: { p: 0, verticalAlign: 'middle' } },
    muiTableBodyRowProps: {
      sx: { cursor: 'pointer' },
    },
    rowCount: meta?.total ?? 0,
    state: {
      columnFilters,
      columnVisibility,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
  });

  const buildFilterParams = (filters: MRT_ColumnFiltersState) => {
    return filters
      .filter(filter => {
        if (filter.value === undefined || filter.value === null) return false;
        if (Array.isArray(filter.value)) return filter.value.some(v => v !== undefined && v !== null);
        return true;
      })
      .map((filter) => {
        let operator = '$contains';
        let value: string | string[] = '';

        if (filter.id === 'created_at') {
          if (Array.isArray(filter.value)) {
            const values = filter.value
              .filter(v => v !== undefined && v !== null)
              .map(v => {
                const date = v instanceof Date ? v : new Date(v);
                return `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              });

            if (values.length === 2) {
              operator = '$between';
              value = values;
            } else if (values.length === 1) {
              if (filter.value[0] && !filter.value[1]) {
                operator = '$gte';
                value = values[0];
              } else if (!filter.value[0] && filter.value[1]) {
                operator = '$lte';
                value = values[0];
              } else {
                return '';
              }
            } else {
              return '';
            }
          } else {
            return '';
          }
        } else {
          value = Array.isArray(filter.value)
            ? filter.value.join(',')
            : filter.value?.toString() || '';
        }

        if (!value) return '';

        if (operator === '$between' && Array.isArray(value) && value.length === 2) {
          return [
            `filters[${filter.id}][$betweenDate][0]=${encodeURIComponent(value[0])}`,
            `filters[${filter.id}][$betweenDate][1]=${encodeURIComponent(value[1])}`
          ].join('&');
        }

        const key = `filters[${filter.id}][${operator}]`;
        return `${key}=${encodeURIComponent(value.toString())}`;
      })
      .filter(Boolean)
      .join('&');
  };

  const buildSortParams = (sortItems: MRT_SortingState) => {
    return sortItems
      .map((sort, index) => {
        const direction = sort.desc ? ":desc" : ":asc";
        return `sort[${index}]=${sort.id}${direction}`;
      })
      .join("&");
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(t('notes.deleteConfirmation', { name }))) {
      deleteMutation.mutate(id);
    }
  };

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_NOTES_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_NOTES_COLUMN_VISIBILITY_KEY,
      JSON.stringify(columnVisibility)
    );
  }, [columnVisibility]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('notes.title')}</Typography>
        <Button
          variant="contained"
          component={Link}
          to="/notes/create"
          startIcon={<AddIcon />}
        >
          {t('notes.create')}
        </Button>
      </Box>

      <MaterialReactTable table={table} />
    </Box>
  );
}; 
